import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import Layout from '../../components/Layout';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../components/ui/dialog';
import { Wrench, Plus, Trash2 } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const NEW_OP = { operation_number: '', description: '', work_center: '', duration_hours: '', activity_rate: '' };
const EMPTY = { description: '', scope: 'General', equipment: '', functional_location: '', operations: [{ ...NEW_OP }], status: 'Active' };

const TaskLists = () => {
  const { canWrite } = useAuth();
  const writable = canWrite('maintenance_task_list');
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try { const res = await axios.get(`${API}/pm/task-lists`, { withCredentials: true }); setRows(res.data); }
    catch (e) { toast.error('Failed to load task lists'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));
  const setOp = (i, k, v) => setForm((p) => { const a = [...p.operations]; a[i] = { ...a[i], [k]: v }; return { ...p, operations: a }; });
  const addOp = () => setForm((p) => ({ ...p, operations: [...p.operations, { ...NEW_OP }] }));
  const removeOp = (i) => setForm((p) => ({ ...p, operations: p.operations.filter((_, x) => x !== i) }));

  const save = async () => {
    setSaving(true);
    try {
      await axios.post(`${API}/pm/task-lists`, {
        description: form.description, scope: form.scope,
        equipment: form.equipment || null, functional_location: form.functional_location || null,
        operations: form.operations.filter((o) => o.operation_number && o.description).map((o) => ({
          operation_number: o.operation_number, description: o.description, work_center: o.work_center || null,
          duration_hours: parseFloat(o.duration_hours) || 0, activity_rate: parseFloat(o.activity_rate) || 0,
        })),
      }, { withCredentials: true });
      toast.success('Task list created'); setOpen(false); setForm(EMPTY); load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to create'); }
    finally { setSaving(false); }
  };

  return (
    <Layout>
      <div className="p-6 space-y-6" data-testid="task-lists-page">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-sky-100 text-sky-700"><Wrench className="h-5 w-5" /></div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Maintenance Task Lists</h1>
              <p className="text-gray-600 text-sm mt-0.5">Standard operation sequences reused in preventive plans.</p>
            </div>
          </div>
          {writable && <Button onClick={() => { setForm(EMPTY); setOpen(true); }} data-testid="new-task-list-btn"><Plus className="h-4 w-4 mr-2" />New Task List</Button>}
        </div>

        <Card className="p-0 overflow-hidden">
          <Table>
            <TableHeader><TableRow>
              <TableHead>Task List #</TableHead><TableHead>Description</TableHead><TableHead>Scope</TableHead>
              <TableHead>Equipment</TableHead><TableHead className="text-right">Operations</TableHead><TableHead>Status</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {loading ? <TableRow><TableCell colSpan={6} className="text-center py-8 text-gray-500">Loading...</TableCell></TableRow>
                : rows.length === 0 ? <TableRow><TableCell colSpan={6} className="text-center py-8 text-gray-500">No task lists yet.</TableCell></TableRow>
                : rows.map((r) => (
                  <TableRow key={r.task_list_number} data-testid={`task-list-row-${r.task_list_number}`}>
                    <TableCell className="font-medium">{r.task_list_number}</TableCell>
                    <TableCell>{r.description}</TableCell>
                    <TableCell>{r.scope}</TableCell>
                    <TableCell>{r.equipment || '—'}</TableCell>
                    <TableCell className="text-right">{r.operations?.length || 0}</TableCell>
                    <TableCell><Badge variant={r.status === 'Active' ? 'default' : 'secondary'}>{r.status}</Badge></TableCell>
                  </TableRow>
                ))}
            </TableBody>
          </Table>
        </Card>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>New Task List</DialogTitle>
            <DialogDescription>Define the standard operations performed during maintenance.</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-3 gap-4 py-2">
            <div className="col-span-2"><Label>Description *</Label><Input data-testid="tl-description" value={form.description} onChange={(e) => set('description', e.target.value)} /></div>
            <div><Label>Equipment</Label><Input data-testid="tl-equipment" value={form.equipment} onChange={(e) => set('equipment', e.target.value)} placeholder="Optional" /></div>
          </div>

          <div className="border-t pt-3">
            <div className="flex items-center justify-between mb-2">
              <h4 className="font-semibold text-gray-900">Operations</h4>
              <Button size="sm" variant="outline" onClick={addOp} data-testid="tl-add-op-btn"><Plus className="h-4 w-4 mr-1" />Add</Button>
            </div>
            <div className="space-y-2">
              {form.operations.map((o, i) => (
                <div key={i} className="grid grid-cols-12 gap-2 items-center" data-testid={`tl-op-row-${i}`}>
                  <Input className="col-span-2" placeholder="Op #" data-testid={`tl-op-number-${i}`} value={o.operation_number} onChange={(e) => setOp(i, 'operation_number', e.target.value)} />
                  <Input className="col-span-4" placeholder="Description" data-testid={`tl-op-desc-${i}`} value={o.description} onChange={(e) => setOp(i, 'description', e.target.value)} />
                  <Input className="col-span-2" placeholder="Work Ctr" value={o.work_center} onChange={(e) => setOp(i, 'work_center', e.target.value)} />
                  <Input className="col-span-2" placeholder="Hrs" type="number" data-testid={`tl-op-hours-${i}`} value={o.duration_hours} onChange={(e) => setOp(i, 'duration_hours', e.target.value)} />
                  <Input className="col-span-1" placeholder="Rate" type="number" value={o.activity_rate} onChange={(e) => setOp(i, 'activity_rate', e.target.value)} />
                  <Button size="sm" variant="ghost" className="col-span-1" onClick={() => removeOp(i)}><Trash2 className="h-4 w-4 text-red-500" /></Button>
                </div>
              ))}
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={save} disabled={saving || !form.description || form.operations.filter((o) => o.operation_number && o.description).length === 0} data-testid="save-task-list-btn">{saving ? 'Saving...' : 'Create'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
};

export default TaskLists;
